import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import {
  Droplets, Percent, Copy, RotateCcw, Info, Calculator as CalcIcon, PieChart,
} from "lucide-react";
import { toast } from "sonner";
import { useUnitSystem, UnitToggle, gToDisplay, displayToG, G_PER_OZ } from "@/lib/units";

// Standard formula components (baker's percentages relative to flour = 100%)
const DEFAULT_FORMULA = {
  hydration: 70,   // water %
  salt: 2,
  yeast: 1,        // instant yeast; sourdough builders may set 0
  sugar: 0,
  fat: 0,
  starter: 20,     // pre-ferment / levain % (assumed 100% hydration)
  starterHydration: 100, // %
};

function fmt(n, d = 0) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d });
}

/**
 * Hydration solver
 * Given any 2 of { flour, water, hydration } compute the third.
 * hydration = water / flour * 100
 */
function solveHydration({ flour, water, hydration, solveFor }) {
  const f = parseFloat(flour), w = parseFloat(water), h = parseFloat(hydration);
  if (solveFor === "water" && !isNaN(f) && !isNaN(h)) return (f * h) / 100;
  if (solveFor === "flour" && !isNaN(w) && !isNaN(h) && h > 0) return (w * 100) / h;
  if (solveFor === "hydration" && !isNaN(f) && !isNaN(w) && f > 0) return (w / f) * 100;
  return null;
}

export default function FormulaCalculator() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [system, setSystem] = useUnitSystem();
  const unit = system === "us" ? "oz" : "g";

  // Section 1: Hydration calculator — user-visible values are stored in whatever unit is selected
  const [solveFor, setSolveFor] = useState("water");
  const [hFlour, setHFlour] = useState(500);
  const [hWater, setHWater] = useState(350);
  const [hHydration, setHHydration] = useState(70);

  // Section 2: Full formula — flourWeight is in the selected display unit
  const [flourWeight, setFlourWeight] = useState(1000);
  const [formula, setFormula] = useState(DEFAULT_FORMULA);
  const [usePreferment, setUsePreferment] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
  }, [user, loading, nav]);

  // Live-solve the "missing" field in section 1
  const hydrationResult = useMemo(
    () => solveHydration({ flour: hFlour, water: hWater, hydration: hHydration, solveFor }),
    [hFlour, hWater, hHydration, solveFor],
  );

  // The final formula weights (deducts flour + water contributed by starter)
  const formulaOutput = useMemo(() => {
    // Convert user-entered flour weight to grams for internal math
    const f = displayToG(flourWeight, system);
    if (!f) return null;

    // Total flour + total water needed by the full formula
    const totalWaterG = (formula.hydration / 100) * f;
    const saltG = (formula.salt / 100) * f;
    const yeastG = (formula.yeast / 100) * f;
    const sugarG = (formula.sugar / 100) * f;
    const fatG = (formula.fat / 100) * f;

    // Starter / pre-ferment
    let starterG = 0, starterFlourG = 0, starterWaterG = 0;
    if (usePreferment && formula.starter > 0) {
      starterG = (formula.starter / 100) * f;
      // starter is X% hydration → water is X/(100+X), flour is 100/(100+X)
      const denom = 100 + parseFloat(formula.starterHydration || 100);
      starterFlourG = starterG * (100 / denom);
      starterWaterG = starterG - starterFlourG;
    }

    // Ingredients to add to the main mix
    const mainFlourG = f - starterFlourG;
    const mainWaterG = totalWaterG - starterWaterG;
    const totalDoughG = f + totalWaterG + saltG + yeastG + sugarG + fatG;

    return {
      totalWaterG, saltG, yeastG, sugarG, fatG,
      starterG, starterFlourG, starterWaterG,
      mainFlourG, mainWaterG, totalDoughG,
    };
  }, [flourWeight, formula, usePreferment]);

  const setFormulaKey = (k, v) => setFormula((prev) => ({ ...prev, [k]: parseFloat(v) || 0 }));

  const copyFormula = () => {
    if (!formulaOutput) return;
    const o = formulaOutput;
    const w = (grams, decimals = 1) => {
      const d = gToDisplay(grams, system);
      return d.value === null ? "—" : `${d.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d.unit === "lb" || d.unit === "kg" ? 2 : decimals })}${d.unit}`;
    };
    const flourG = displayToG(flourWeight, system);
    const lines = [];
    lines.push(`Formula for ${w(flourG, 0)} flour`);
    lines.push(`Total dough: ${w(o.totalDoughG)} · Hydration: ${formula.hydration}%`);
    lines.push("");
    if (usePreferment && formula.starter > 0) {
      lines.push(`Pre-ferment / starter (${formula.starterHydration}%):`);
      lines.push(`• Total: ${w(o.starterG)} (flour ${w(o.starterFlourG)} + water ${w(o.starterWaterG)})`);
      lines.push("");
      lines.push("Main mix (add):");
      lines.push(`• Flour: ${w(o.mainFlourG)}`);
      lines.push(`• Water: ${w(o.mainWaterG)}`);
    } else {
      lines.push(`• Flour: ${w(flourG, 0)}`);
      lines.push(`• Water: ${w(o.totalWaterG)} (${formula.hydration}%)`);
    }
    lines.push(`• Salt: ${w(o.saltG, 2)} (${formula.salt}%)`);
    if (formula.yeast > 0) lines.push(`• Yeast: ${w(o.yeastG, 2)} (${formula.yeast}%)`);
    if (formula.sugar > 0) lines.push(`• Sugar: ${w(o.sugarG, 2)} (${formula.sugar}%)`);
    if (formula.fat > 0) lines.push(`• Fat: ${w(o.fatG, 2)} (${formula.fat}%)`);
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Formula copied");
  };

  const resetAll = () => {
    if (system === "us") {
      setHFlour(18); setHWater(12.5); setHHydration(70); setSolveFor("water");
      setFlourWeight(35); setFormula(DEFAULT_FORMULA); setUsePreferment(true);
    } else {
      setHFlour(500); setHWater(350); setHHydration(70); setSolveFor("water");
      setFlourWeight(1000); setFormula(DEFAULT_FORMULA); setUsePreferment(true);
    }
  };

  if (loading || !user) return null;

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <Droplets className="w-3.5 h-3.5" style={{ color: "#2F80ED" }} />
              <span>Baker's tool</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              Hydration & <span style={{ color: "#2F80ED" }}>Formula</span> Calculator
            </h1>
            <p className="mt-2 text-[#857163] max-w-2xl">
              Quick baker's math for dough consistency. Solve any hydration equation, or build a full formula from baker's percentages to gram weights — with pre-ferment support.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <UnitToggle system={system} onChange={setSystem} testid="calc-unit-toggle" />
            <button
              onClick={resetAll}
              className="cp-btn-ghost text-sm inline-flex items-center gap-1.5"
              data-testid="reset-btn"
            ><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
          </div>
        </div>

        {/* Section 1: Hydration Calculator */}
        <section className="cp-card p-5 sm:p-6 mb-6" data-testid="hydration-calc">
          <div className="flex items-center gap-2 mb-3">
            <Droplets className="w-5 h-5" style={{ color: "#2F80ED" }} />
            <h2 className="font-heading text-2xl text-[#3B2C24]">Hydration solver</h2>
          </div>
          <p className="text-sm text-[#857163] mb-4">Enter any two, and we'll solve the third.</p>

          <div className="flex gap-1.5 flex-wrap mb-4">
            {[
              { key: "water", label: "Solve for water" },
              { key: "flour", label: "Solve for flour" },
              { key: "hydration", label: "Solve for hydration %" },
            ].map((m) => (
              <button
                key={m.key}
                onClick={() => setSolveFor(m.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  solveFor === m.key
                    ? "bg-[#2F80ED] text-white border-[#2F80ED]"
                    : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                }`}
                data-testid={`solve-for-${m.key}`}
              >{m.label}</button>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <HInput
              label={`Flour weight (${unit})`}
              value={hFlour}
              onChange={setHFlour}
              disabled={solveFor === "flour"}
              solved={solveFor === "flour" ? hydrationResult : null}
              testid="h-flour"
            />
            <HInput
              label={`Water weight (${unit})`}
              value={hWater}
              onChange={setHWater}
              disabled={solveFor === "water"}
              solved={solveFor === "water" ? hydrationResult : null}
              testid="h-water"
            />
            <HInput
              label="Hydration (%)"
              value={hHydration}
              onChange={setHHydration}
              disabled={solveFor === "hydration"}
              solved={solveFor === "hydration" ? hydrationResult : null}
              step={0.5}
              testid="h-hydration"
            />
          </div>

          {hydrationResult !== null && (
            <div className="mt-4 p-4 rounded-2xl flex items-center justify-between gap-3 flex-wrap" style={{ background: "rgba(47,128,237,0.08)" }}>
              <div className="text-sm text-[#3B2C24]">
                {solveFor === "water" && <>You need <b>{fmt(hydrationResult, 1)}{unit} water</b> for {fmt(hFlour)}{unit} flour at {hHydration}% hydration.</>}
                {solveFor === "flour" && <>You need <b>{fmt(hydrationResult, 1)}{unit} flour</b> to hit {hHydration}% hydration with {fmt(hWater)}{unit} water.</>}
                {solveFor === "hydration" && <>Your dough is at <b>{fmt(hydrationResult, 1)}% hydration</b> ({fmt(hWater)}{unit} water / {fmt(hFlour)}{unit} flour).</>}
              </div>
              <HydrationBadge percent={solveFor === "hydration" ? hydrationResult : parseFloat(hHydration)} />
            </div>
          )}

          <div className="mt-4 text-[11px] text-[#857163] flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>Rough guides: 55–65% = stiff (bagels), 65–72% = standard bread, 72–80% = high-hydration open crumb, 80%+ = ciabatta / focaccia territory.</span>
          </div>
        </section>

        {/* Section 2: Formula builder */}
        <section className="cp-card p-5 sm:p-6" data-testid="formula-builder">
          <div className="flex items-center gap-2 mb-3">
            <Percent className="w-5 h-5" style={{ color: "#8EAC50" }} />
            <h2 className="font-heading text-2xl text-[#3B2C24]">Formula → weights</h2>
          </div>
          <p className="text-sm text-[#857163] mb-4">Set your flour weight and baker's percentages. We'll compute every ingredient in grams — with pre-ferment adjustment.</p>

          <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6">
            {/* Left: percentages */}
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs text-[#857163]">Total flour weight ({unit})</span>
                <input
                  type="number"
                  min={0}
                  className="cp-input mt-1"
                  value={flourWeight}
                  onChange={(e) => setFlourWeight(e.target.value)}
                  data-testid="flour-weight-input"
                />
              </label>

              <PctRow label="Hydration (water)" value={formula.hydration} onChange={(v) => setFormulaKey("hydration", v)} accent="#2F80ED" testid="pct-hydration" />
              <PctRow label="Salt" value={formula.salt} onChange={(v) => setFormulaKey("salt", v)} step={0.1} testid="pct-salt" />
              <PctRow label="Yeast (instant)" value={formula.yeast} onChange={(v) => setFormulaKey("yeast", v)} step={0.05} testid="pct-yeast" />
              <PctRow label="Sugar" value={formula.sugar} onChange={(v) => setFormulaKey("sugar", v)} step={0.5} testid="pct-sugar" />
              <PctRow label="Fat (oil/butter)" value={formula.fat} onChange={(v) => setFormulaKey("fat", v)} step={0.5} testid="pct-fat" />

              <label className="flex items-center gap-2 pt-2 border-t border-[#EBDCD1]">
                <input
                  type="checkbox"
                  checked={usePreferment}
                  onChange={(e) => setUsePreferment(e.target.checked)}
                  className="w-4 h-4 accent-[#8EAC50]"
                  data-testid="use-preferment"
                />
                <span className="text-sm text-[#3B2C24] font-medium">Use pre-ferment / starter</span>
              </label>
              {usePreferment && (
                <div className="pl-6 space-y-2">
                  <PctRow label="Pre-ferment amount" value={formula.starter} onChange={(v) => setFormulaKey("starter", v)} testid="pct-starter" hint="% of total flour" />
                  <PctRow label="Pre-ferment hydration" value={formula.starterHydration} onChange={(v) => setFormulaKey("starterHydration", v)} testid="pct-starter-hydration" hint="100% = equal flour + water" />
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="hidden lg:block w-px bg-[#EBDCD1]" />

            {/* Right: weights output */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="text-xs uppercase tracking-widest text-[#857163]">Weights</div>
                {formulaOutput && (
                  <button
                    onClick={copyFormula}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white hover:opacity-90 transition"
                    style={{ background: "#8EAC50" }}
                    data-testid="copy-formula-btn"
                  ><Copy className="w-3 h-3" /> Copy</button>
                )}
              </div>

              {!formulaOutput ? (
                <div className="text-sm text-[#857163]">Enter a flour weight above to see the formula in {unit}.</div>
              ) : (
                <div className="space-y-3">
                  {usePreferment && formula.starter > 0 && (
                    <div className="p-3 rounded-2xl bg-[#F9EFE6] border border-[#EBDCD1]">
                      <div className="text-xs font-semibold text-[#3B2C24] mb-1.5">Pre-ferment ({formula.starterHydration}%)</div>
                      <WRow name="Total starter" value={formulaOutput.starterG} system={system} />
                      <WRow name="  from flour" value={formulaOutput.starterFlourG} indent system={system} />
                      <WRow name="  from water" value={formulaOutput.starterWaterG} indent system={system} />
                    </div>
                  )}

                  <div className="p-3 rounded-2xl border border-[#EBDCD1]">
                    <div className="text-xs font-semibold text-[#3B2C24] mb-1.5">Main mix (add to preferment)</div>
                    <WRow name="Flour" value={usePreferment ? formulaOutput.mainFlourG : displayToG(flourWeight, system)} strong system={system} />
                    <WRow name="Water" value={usePreferment ? formulaOutput.mainWaterG : formulaOutput.totalWaterG} strong system={system} />
                    <WRow name="Salt" value={formulaOutput.saltG} system={system} />
                    {formula.yeast > 0 && <WRow name="Yeast" value={formulaOutput.yeastG} system={system} />}
                    {formula.sugar > 0 && <WRow name="Sugar" value={formulaOutput.sugarG} system={system} />}
                    {formula.fat > 0 && <WRow name="Fat" value={formulaOutput.fatG} system={system} />}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <MetricW label="Total flour" grams={displayToG(flourWeight, system)} system={system} />
                    <MetricW label="Total water" grams={formulaOutput.totalWaterG} system={system} />
                    <Metric label="Hydration" value={`${formula.hydration}%`} accent="#2F80ED" />
                    <MetricW label="Dough weight" grams={formulaOutput.totalDoughG} system={system} accent="#8EAC50" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 text-[11px] text-[#857163] flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>Total water already <b>includes</b> the water inside your pre-ferment. Main-mix flour and water are what you add on top of your starter/levain.</span>
          </div>
        </section>
      </main>
    </>
  );
}

function HInput({ label, value, onChange, disabled, solved, step = 1, testid }) {
  const displayed = disabled && solved !== null ? solved.toFixed(1) : value;
  return (
    <div>
      <label className="text-xs text-[#857163]">{label}</label>
      <input
        type="number"
        min={0}
        step={step}
        value={disabled ? displayed : value}
        onChange={(e) => !disabled && onChange(e.target.value)}
        disabled={disabled}
        readOnly={disabled}
        className="cp-input mt-1 tabular-nums"
        style={disabled ? { background: "rgba(47,128,237,0.10)", color: "#2F80ED", fontWeight: 700 } : {}}
        data-testid={testid}
      />
      {disabled && <div className="text-[10px] text-[#2F80ED] mt-1 uppercase tracking-widest">Computed</div>}
    </div>
  );
}

function PctRow({ label, value, onChange, step = 0.5, accent, hint, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs text-[#857163]">{label}</label>
        {hint && <span className="text-[10px] text-[#857163] italic">{hint}</span>}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="number"
          min={0}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="cp-input tabular-nums"
          style={accent ? { borderColor: accent + "44" } : {}}
          data-testid={testid}
        />
        <span className="text-sm font-semibold" style={{ color: accent || "#857163" }}>%</span>
      </div>
    </div>
  );
}

function WRow({ name, value, indent, strong, system }) {
  const d = gToDisplay(value, system);
  return (
    <div className={`flex items-center justify-between text-sm py-0.5 ${indent ? "text-[#857163]" : "text-[#3B2C24]"}`}>
      <span>{name}</span>
      <span className={`font-mono tabular-nums ${strong ? "font-bold" : ""}`}>
        {d.value === null ? "—" : d.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d.unit === "lb" || d.unit === "kg" ? 2 : 1 })}<span className="text-[10px] text-[#857163] ml-0.5">{d.unit}</span>
      </span>
    </div>
  );
}

function MetricW({ label, grams, system, accent }) {
  const d = gToDisplay(grams, system);
  const value = d.value === null ? "—" : d.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d.unit === "lb" || d.unit === "kg" ? 2 : 1 });
  return <Metric label={label} value={value} suffix={d.unit} accent={accent} />;
}

function Metric({ label, value, suffix, accent }) {
  return (
    <div className="p-3 rounded-2xl border border-[#EBDCD1]">
      <div className="text-[10px] uppercase tracking-widest text-[#857163]">{label}</div>
      <div className="font-heading text-lg font-bold mt-0.5 tabular-nums" style={{ color: accent || "#3B2C24" }}>
        {value}{suffix && <span className="text-xs text-[#857163] ml-1 font-normal">{suffix}</span>}
      </div>
    </div>
  );
}

function HydrationBadge({ percent }) {
  let label = "", color = "#857163";
  const p = parseFloat(percent);
  if (isNaN(p)) return null;
  if (p < 55) { label = "Very stiff"; color = "#7A5230"; }
  else if (p < 65) { label = "Stiff (bagels)"; color = "#B5834B"; }
  else if (p < 72) { label = "Standard"; color = "#4C9F70"; }
  else if (p < 80) { label = "High hydration"; color = "#2F80ED"; }
  else { label = "Very wet"; color = "#C2185B"; }
  return (
    <span className="px-3 py-1 rounded-full text-xs font-semibold text-white shrink-0" style={{ background: color }}>
      {label} · {p.toFixed(0)}%
    </span>
  );
}
